import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { useOpsMe } from "../api/me.js";
import { BrandLogo } from "../ui/BrandLogo.js";

/**
 * Authenticated console shell — left rail nav + top bar with the signed-in
 * staffer and sign-out. Desktop-first (staff on laptops). Nav targets are
 * placeholders until their screens land (Contractors in O3, Customers and
 * Insights later); they render an "coming soon" stub for now.
 */
const navItems = [
  { to: "/contractors", label: "Contractors" },
  { to: "/customers", label: "Customers" },
  // Sits directly under Customers because it is the same work seen from the
  // other end: these are customers whose job went wrong and who nobody has
  // called yet. admin|support, matching who the API lets read it.
  { to: "/follow-ups", label: "Follow-ups", staffOnly: true },
  { to: "/insights", label: "Insights" },
  // Managing who can sign in is an admin power, so the tab only appears for
  // admins. The page and the API enforce it independently — this just keeps
  // the rail honest about what the signed-in staffer can actually open.
  { to: "/team", label: "Team", adminOnly: true },
] as const;

export function Shell() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { data: me } = useOpsMe();
  const isAdmin = (me?.roles ?? []).includes("admin");
  const canTriage = isAdmin || (me?.roles ?? []).includes("support");
  const visibleNavItems = navItems.filter(
    (item) =>
      (!("adminOnly" in item) || isAdmin) &&
      // The API is admin|support; an analyst following this tab would only
      // ever get a generic load error, re-fired every minute.
      (!("staffOnly" in item) || canTriage),
  );

  async function onSignOut() {
    await signOut();
    navigate("/sign-in", { replace: true });
  }

  return (
    <div className="flex min-h-dvh bg-field-gray">
      <aside className="hidden w-56 shrink-0 flex-col border-r border-charcoal/15 bg-white sm:flex">
        <div className="flex items-center gap-3 px-5 py-4">
          <BrandLogo variant="shield" />
          <p className="text-sm font-bold text-charcoal">Operations</p>
        </div>
        <nav className="flex-1 px-2">
          {visibleNavItems.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                [
                  "block rounded-lg px-3 py-2 text-sm font-medium",
                  isActive ? "bg-brand-50 text-brand-800" : "text-charcoal hover:bg-field-gray",
                ].join(" ")
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b border-charcoal/15 bg-white px-5">
          <div className="flex items-center gap-4 sm:hidden">
            <BrandLogo variant="shield" />
            <nav className="flex gap-3">
              {visibleNavItems.map(({ to, label }) => (
                <NavLink key={to} to={to} className="text-sm font-medium text-charcoal">
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="ml-auto flex items-center gap-3 text-sm">
            {me && (
              <span className="text-charcoal">
                {me.email}
                {me.roles.length > 0 && (
                  <span className="ml-2 rounded-full bg-field-gray px-2 py-0.5 text-xs font-medium text-charcoal">
                    {me.roles.join(", ")}
                  </span>
                )}
              </span>
            )}
            <button
              type="button"
              onClick={() => void onSignOut()}
              className="rounded-lg border border-charcoal/30 px-3 py-1.5 font-medium text-charcoal hover:bg-field-gray"
            >
              Sign out
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
